import { fetchApi } from '~/composables/useFetchApi';

type SecureRouteValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean | null | undefined>;

export interface SecureRouteContext {
  token: string;
  type:
    | 'user'
    | 'project'
    | 'project-home'
    | 'form'
    | 'submission'
    | 'workflow'
    | 'project-form'
    | 'distribution-group'
    | 'dashboard'
    | 'company'
    | 'company-contacts'
    | 'role'
    | 'custom-list'
    | 'wbs-project'
    | 'project-directory'
    | 'project-directory-contacts'
    | 'project-header-footer'
    | 'project-assignees';
  action: 'view' | 'update' | 'list' | 'add' | 'versions';
  layout?: string;
  params?: Record<string, SecureRouteValue>;
  query?: Record<string, SecureRouteValue>;
}

interface SecureRouteApiResponse {
  token?: string;
  url?: string;
  data?: {
    token?: string;
    url?: string;
    data?: {
      token?: string;
      url?: string;
    };
  };
}

export interface SecureRoutePayload {
  type: SecureRouteContext['type'];
  id: number | string;
  action: SecureRouteContext['action'];
  projectId?: number | string | null;
  formVersionId?: number | string | null;
  moduleId?: number | string | null;
}

export interface SecureRouteNavigationOptions {
  query?: Record<string, SecureRouteValue>;
  /** Called when the secure-route endpoint denies access with HTTP 403. */
  onPermissionDenied?: () => void | Promise<void>;
  /**
   * Runs only once the secure-route permission check and navigation both
   * succeed. This lets callers defer side effects (such as marking an inbox
   * item as read) until the destination was actually opened.
   */
  onNavigated?: () => void | Promise<void>;
}

export interface ResolvedSecureRouteLink {
  payload: SecureRoutePayload;
  query?: Record<string, SecureRouteValue>;
}

const firstValue = (
  value: SecureRouteValue,
): string | number | boolean | null => {
  if (Array.isArray(value)) {
    const first = value.find((item) => item !== null && item !== undefined);
    return first ?? null;
  }

  return value ?? null;
};

export const secureRouteScalar = (value: unknown): string | number | null => {
  const values = Array.isArray(value) ? value.flat(Infinity) : [value];

  for (const item of values) {
    if (typeof item === 'string') {
      const normalized = item.trim().toLowerCase();

      if (normalized && !['null', 'undefined', 'nan'].includes(normalized)) {
        return item;
      }
    }

    if (typeof item === 'number' && Number.isFinite(item)) {
      return item;
    }
  }

  return null;
};

export const secureRouteText = (value: unknown, fallback = ''): string => {
  const item = secureRouteScalar(value);

  return item === null ? fallback : String(item);
};

const compactKey = (value: string) => value.replace(/[-_]/g, '').toLowerCase();

const pickFromRecord = (
  record: Record<string, any> | undefined,
  keys: string[],
) => {
  if (!record) return null;

  for (const key of keys) {
    const value = firstValue(record[key]);
    if (value !== null) return value;
  }

  const normalizedRecord = Object.entries(record).reduce<Record<string, any>>(
    (carry, [key, value]) => {
      carry[compactKey(key)] = value;
      return carry;
    },
    {},
  );

  for (const key of keys) {
    const value = firstValue(normalizedRecord[compactKey(key)]);
    if (value !== null) return value;
  }

  return null;
};

const appendQuery = (
  url: string,
  query: Record<string, SecureRouteValue> | undefined,
) => {
  if (!query) return url;

  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value : [value];

    values.forEach((item) => {
      if (item === null || item === undefined || item === '') return;
      params.append(key, String(item));
    });
  });

  const queryString = params.toString();
  if (!queryString) return url;

  return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
};

const secureRouteErrorStatus = (error: any) =>
  Number(
    error?.statusCode ??
      error?.status ??
      error?.data?.statusCode ??
      error?.data?.status ??
      error?.response?.statusCode ??
      error?.response?.status ??
      error?.response?._data?.code,
  );

export const isPermissionDeniedError = (error: any) =>
  secureRouteErrorStatus(error) === 403;

const toAction = (value: string): SecureRouteContext['action'] | null => {
  if (
    value === 'view' ||
    value === 'update' ||
    value === 'list' ||
    value === 'add' ||
    value === 'versions'
  ) {
    return value;
  }

  return null;
};

const uiQueryFromSearchParams = (searchParams: URLSearchParams) => {
  const query: Record<string, SecureRouteValue> = {};

  ['tab', 'cycle'].forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      query[key] = value;
    }
  });

  return Object.keys(query).length ? query : undefined;
};

const normalizedInternalLink = (rawLink: string) => {
  try {
    const parsed = new URL(rawLink, 'http://secure-route.local');
    const pathname =
      parsed.pathname.replace(/^\/(?:en|ar)(?=\/)/, '').replace(/\/+$/, '') ||
      '/';

    return {
      pathname,
      searchParams: parsed.searchParams,
    };
  } catch {
    return null;
  }
};

export const resolveSecureRouteLink = (
  rawLink: string,
): ResolvedSecureRouteLink | null => {
  const parsed = normalizedInternalLink(rawLink);
  if (!parsed) return null;

  const { pathname, searchParams } = parsed;
  const userMatch = pathname.match(/^\/users\/(\d+)\/(view|update)$/);
  if (userMatch) {
    const action = toAction(userMatch[2]!);
    if (!action) return null;

    return {
      payload: { type: 'user', id: userMatch[1]!, action },
    };
  }

  const formMatch = pathname.match(/^\/forms\/(\d+)\/(view|update)$/);
  if (formMatch) {
    const action = toAction(formMatch[2]!);
    if (!action) return null;

    return {
      payload: { type: 'form', id: formMatch[1]!, action },
    };
  }

  const formVersionsMatch = pathname.match(/^\/forms\/(\d+)\/versions$/);
  if (formVersionsMatch) {
    return {
      payload: {
        type: 'form',
        id: formVersionsMatch[1]!,
        action: 'versions',
      },
    };
  }

  const projectMatch = pathname.match(
    /^\/wbs\/\d+\/projects\/(\d+)\/(view|update)$/,
  );
  if (projectMatch) {
    const action = toAction(projectMatch[2]!);
    if (!action) return null;

    return {
      payload: { type: 'project', id: projectMatch[1]!, action },
    };
  }

  const projectHomeMatch = pathname.match(/^\/projects\/(\d+)$/);
  if (projectHomeMatch) {
    return {
      payload: {
        type: 'project-home',
        id: projectHomeMatch[1]!,
        action: 'view',
      },
    };
  }

  const projectAssigneesMatch = pathname.match(
    /^\/wbs\/(\d+)\/projects\/(\d+)\/assignees$/,
  );
  if (projectAssigneesMatch) {
    return {
      payload: {
        type: 'project-assignees',
        id: projectAssigneesMatch[2]!,
        action: 'update',
      },
    };
  }

  const dashboardMatch = pathname.match(/^\/dashboard\/(\d+)$/);
  if (dashboardMatch) {
    return {
      payload: {
        type: 'dashboard',
        id: dashboardMatch[1]!,
        action: 'view',
      },
    };
  }

  const submissionMatch = pathname.match(
    /^\/projects\/\d+\/forms\/\d+\/submission\/(view|update)$/,
  );
  if (submissionMatch) {
    const action = toAction(submissionMatch[1]!);
    const submissionId = searchParams.get('submissionId');
    if (!action || !submissionId) return null;

    return {
      payload: { type: 'submission', id: submissionId, action },
      query: uiQueryFromSearchParams(searchParams),
    };
  }

  const workflowMatch = pathname.match(
    /^\/projects\/(\d+)\/workflow\/(\d+)\/(view|update)$/,
  );
  if (workflowMatch) {
    const action = toAction(workflowMatch[3]!);
    if (!action) return null;

    return {
      payload: {
        type: 'workflow',
        id: workflowMatch[2]!,
        action,
        projectId: workflowMatch[1]!,
      },
    };
  }

  const workflowVersionsMatch = pathname.match(
    /^\/projects\/(\d+)\/workflow\/(\d+)\/versions$/,
  );
  if (workflowVersionsMatch) {
    return {
      payload: {
        type: 'workflow',
        id: workflowVersionsMatch[2]!,
        action: 'versions',
        projectId: workflowVersionsMatch[1]!,
      },
    };
  }

  const projectWorkflowMatch = pathname.match(
    /^\/projects\/(\d+)\/workflow(?:\/(add))?$/,
  );
  if (projectWorkflowMatch) {
    return {
      payload: {
        type: 'workflow',
        id: projectWorkflowMatch[1]!,
        action: projectWorkflowMatch[2] === 'add' ? 'add' : 'list',
      },
    };
  }

  const projectFormSubmissionMatch = pathname.match(
    /^\/projects\/(\d+)\/forms\/(\d+)\/submission(?:\/(add))?$/,
  );
  if (projectFormSubmissionMatch) {
    return {
      payload: {
        type: 'project-form',
        id: projectFormSubmissionMatch[2]!,
        action: projectFormSubmissionMatch[3] === 'add' ? 'add' : 'list',
        projectId: projectFormSubmissionMatch[1]!,
        formVersionId: searchParams.get('version') ?? searchParams.get('id'),
        moduleId: searchParams.get('module'),
      },
    };
  }

  const distributionGroupMatch = pathname.match(
    /^\/projects\/(\d+)\/distribution-group\/(\d+)\/(view|update)$/,
  );
  if (distributionGroupMatch) {
    const action = toAction(distributionGroupMatch[3]!);
    if (!action) return null;

    return {
      payload: {
        type: 'distribution-group',
        id: distributionGroupMatch[2]!,
        action,
        projectId: distributionGroupMatch[1]!,
      },
    };
  }

  const projectDistributionGroupMatch = pathname.match(
    /^\/projects\/(\d+)\/distribution-group(?:\/(add))?$/,
  );
  if (projectDistributionGroupMatch) {
    return {
      payload: {
        type: 'distribution-group',
        id: projectDistributionGroupMatch[1]!,
        action: projectDistributionGroupMatch[2] === 'add' ? 'add' : 'list',
      },
    };
  }

  const companyMatch = pathname.match(
    /^\/enterprise-directory\/(\d+)\/(view|update)$/,
  );
  if (companyMatch) {
    const action = toAction(companyMatch[2]!);
    if (!action) return null;

    return {
      payload: {
        type: 'company',
        id: companyMatch[1]!,
        action,
      },
    };
  }

  const companyContactsMatch = pathname.match(
    /^\/enterprise-directory\/(\d+)\/contacts$/,
  );
  if (companyContactsMatch) {
    return {
      payload: {
        type: 'company-contacts',
        id: companyContactsMatch[1]!,
        action: 'list',
      },
    };
  }

  const roleMatch = pathname.match(/^\/roles\/(\d+)\/(view|update)$/);
  if (roleMatch) {
    const action = toAction(roleMatch[2]!);
    if (!action) return null;

    return {
      payload: {
        type: 'role',
        id: roleMatch[1]!,
        action,
      },
    };
  }

  const customListItemsMatch = pathname.match(/^\/custom-lists\/(\d+)\/items$/);
  if (customListItemsMatch) {
    return {
      payload: {
        type: 'custom-list',
        id: customListItemsMatch[1]!,
        action: 'list',
      },
    };
  }

  const wbsProjectMatch = pathname.match(
    /^\/wbs\/(\d+)\/projects(?:\/(add))?$/,
  );
  if (wbsProjectMatch) {
    return {
      payload: {
        type: 'wbs-project',
        id: wbsProjectMatch[1]!,
        action: wbsProjectMatch[2] === 'add' ? 'add' : 'list',
      },
    };
  }

  const projectDirectoryCompanyMatch = pathname.match(
    /^\/projects\/(\d+)\/project-directory\/(\d+)\/(view|update)$/,
  );
  if (projectDirectoryCompanyMatch) {
    const action = toAction(projectDirectoryCompanyMatch[3]!);
    if (!action) return null;

    return {
      payload: {
        type: 'project-directory',
        id: projectDirectoryCompanyMatch[2]!,
        action,
        projectId: projectDirectoryCompanyMatch[1]!,
      },
    };
  }

  const projectDirectoryContactsMatch = pathname.match(
    /^\/projects\/(\d+)\/project-directory\/(\d+)\/contacts$/,
  );
  if (projectDirectoryContactsMatch) {
    return {
      payload: {
        type: 'project-directory-contacts',
        id: projectDirectoryContactsMatch[2]!,
        action: 'list',
        projectId: projectDirectoryContactsMatch[1]!,
      },
    };
  }

  const projectDirectoryMatch = pathname.match(
    /^\/projects\/(\d+)\/project-directory(?:\/(add))?$/,
  );
  if (projectDirectoryMatch) {
    return {
      payload: {
        type: 'project-directory',
        id: projectDirectoryMatch[1]!,
        action: projectDirectoryMatch[2] === 'add' ? 'add' : 'list',
      },
    };
  }

  const projectHeaderFooterMatch = pathname.match(
    /^\/projects\/(\d+)\/header-footer$/,
  );
  if (projectHeaderFooterMatch) {
    return {
      payload: {
        type: 'project-header-footer',
        id: projectHeaderFooterMatch[1]!,
        action: 'list',
      },
    };
  }

  return null;
};

const secureRouteContextState = () =>
  useState<SecureRouteContext | null>('secure-route-context', () => null);

export const clearSecureRouteContext = () => {
  secureRouteContextState().value = null;
};

export const useSecureRouteContext = () => {
  const context = secureRouteContextState();

  const set = (value: SecureRouteContext) => {
    context.value = value;
  };

  const clear = () => {
    clearSecureRouteContext();
  };

  const param = (...keys: string[]) =>
    pickFromRecord(context.value?.params, keys) ??
    pickFromRecord(context.value as Record<string, any> | undefined, keys);

  const query = (...keys: string[]) =>
    pickFromRecord(context.value?.query, keys);

  return {
    context,
    hasContext: computed(() => context.value !== null),
    set,
    clear,
    param,
    query,
  };
};

export const navigateToSecureRoute = async (
  payload: SecureRoutePayload,
  options: SecureRouteNavigationOptions = {},
) => {
  const localePath = useLocalePath();
  let response: SecureRouteApiResponse | undefined;

  try {
    response = await fetchApi<SecureRouteApiResponse>('secure-route', {
      method: 'POST',
      body: payload,
    });
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      await options.onPermissionDenied?.();
      return;
    }

    throw error;
  }

  const url = response?.data?.url ?? response?.url ?? response?.data?.data?.url;
  if (!url) {
    throw new Error('Secure route token was not returned.');
  }

  const navigationResult = await navigateTo(
    localePath(appendQuery(url, options.query)),
  );

  // Vue Router resolves successful navigation with `undefined`; a cancelled
  // or aborted navigation resolves with a failure object (or `false`).
  if (navigationResult === undefined) {
    await options.onNavigated?.();
  }

  return navigationResult;
};

export const navigateToSecureRouteLink = async (
  rawLink: string,
  options: SecureRouteNavigationOptions = {},
) => {
  const localePath = useLocalePath();
  const resolved = resolveSecureRouteLink(rawLink);

  if (resolved) {
    return navigateToSecureRoute(resolved.payload, {
      ...options,
      query: { ...resolved.query, ...options.query },
    });
  }

  const navigationResult = await navigateTo(localePath(rawLink));
  if (navigationResult === undefined) {
    await options.onNavigated?.();
  }

  return navigationResult;
};
